import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { BulkCreateFinancialYearJobStatusDto, BulkCreateFinancialYearPreviewDto, BulkCreateStatus, ProjectPreview } from '@modeso/aumico-lib-annualreport-fe';
import { I18NextService } from 'angular-i18next';

interface GroupedPreview {
    year: string;
    previews: ProjectPreview[];
    count: number;
}

@Component({
    selector: 'bulk-create-financial-year-modal',
    templateUrl: './bulk-create-financial-year-modal.component.html',
    styleUrls: ['./bulk-create-financial-year-modal.component.scss'],
})
export class BulkCreateFinancialYearModalComponent implements OnInit, OnChanges {
    @Input() previewData: BulkCreateFinancialYearPreviewDto | null = null;
    @Input() loading: boolean = false;
    @Input() jobStatus: BulkCreateFinancialYearJobStatusDto | null = null;
    @Input() progress: number = 0;
    @Output() confirm = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    BulkCreateStatus = BulkCreateStatus;
    readyGroups: GroupedPreview[] = [];
    attentionGroups: GroupedPreview[] = [];
    skipPreviews: ProjectPreview[] = [];

    constructor(private i18next: I18NextService) { }

    get isProcessing(): boolean {
        return this.jobStatus != null && (this.jobStatus.status === 'pending' || this.jobStatus.status === 'processing');
    }

    get showPreview(): boolean {
        return this.previewData != null && !this.isProcessing;
    }

    ngOnInit(): void {
        if (this.previewData) {
            this.groupPreviews();
        }
    }

    ngOnChanges(): void {
        if (this.previewData) {
            this.groupPreviews();
        }
    }

    private groupPreviews(): void {
        if (!this.previewData) return;

        const readyByYear = new Map<string, ProjectPreview[]>();
        const attentionByYear = new Map<string, ProjectPreview[]>();
        this.skipPreviews = [];

        this.previewData.previews.forEach((preview) => {
            if (preview.status === BulkCreateStatus.READY) {
                const year = preview.targetYear || 'Unknown';
                if (!readyByYear.has(year)) {
                    readyByYear.set(year, []);
                }
                readyByYear.get(year)!.push(preview);
            } else if (preview.status === BulkCreateStatus.ATTENTION) {
                const year = preview.targetYear || 'Unknown';
                if (!attentionByYear.has(year)) {
                    attentionByYear.set(year, []);
                }
                attentionByYear.get(year)!.push(preview);
            } else if (preview.status === BulkCreateStatus.SKIP) {
                this.skipPreviews.push(preview);
            }
        });

        // Convert maps to grouped arrays and sort by year
        this.readyGroups = Array.from(readyByYear.entries())
            .map(([year, previews]) => ({
                year,
                previews,
                count: previews.length,
            }))
            .sort((a, b) => b.year.localeCompare(a.year));

        this.attentionGroups = Array.from(attentionByYear.entries())
            .map(([year, previews]) => ({
                year,
                previews,
                count: previews.length,
            }))
            .sort((a, b) => b.year.localeCompare(a.year));
    }

    onConfirm(): void {
        this.confirm.emit();
    }

    onCancel(): void {
        this.cancel.emit();
    }

    getLocalizedReason(preview: ProjectPreview): string {
        const reason = preview.reason?.trim();
        if (!reason) return '';

        const statusMatch = reason.match(
            /Company status is '(.+?)'\. Only active companies can have financial years created\./,
        );
        if (statusMatch) {
            return this.i18next
                .t('aumico_companies_bulk_create_reason_company_status_only_active', {
                    status: statusMatch[1],
                })
                .toString();
        }

        const mandateMatch = reason.match(
            /Company mandate expired on (.+?)\. Cannot create financial year for expired mandates\./,
        );
        if (mandateMatch) {
            return this.i18next
                .t('aumico_companies_bulk_create_reason_mandate_expired', {
                    mandateEndDate: mandateMatch[1],
                })
                .toString();
        }

        const invalidYearMatch = reason.match(
            /Invalid year (.+?): must be within reasonable range \((.+?) to (.+?)\)/,
        );
        if (invalidYearMatch) {
            return this.i18next
                .t('aumico_companies_bulk_create_reason_invalid_year_range', {
                    nextYear: invalidYearMatch[1],
                    minYear: invalidYearMatch[2],
                    maxYear: invalidYearMatch[3],
                })
                .toString();
        }

        switch (reason) {
            case 'Error calculating preview':
                return this.i18next.t('aumico_companies_bulk_create_reason_error_calculating_preview').toString();
            case 'Access denied: You do not have permission to access this company':
                return this.i18next.t('aumico_companies_bulk_create_reason_access_denied').toString();
            case 'Error retrieving company information':
                return this.i18next.t('aumico_companies_bulk_create_reason_error_retrieving_company_info').toString();
            case 'No previous project found':
                return this.i18next.t('aumico_companies_bulk_create_reason_no_previous_project').toString();
            case 'Previous project has no saved general information in its annual report':
                return this.i18next
                    .t('aumico_companies_bulk_create_reason_previous_project_no_general_info')
                    .toString();
            case 'Invalid date range: start date must be before end date':
                return this.i18next.t('aumico_companies_bulk_create_reason_invalid_date_range').toString();
            case 'Project for this period already exists':
                return this.i18next.t('aumico_companies_bulk_create_reason_project_already_exists').toString();
            case 'Non-calendar fiscal year (does not start on January 1st)':
                return this.i18next.t('aumico_companies_bulk_create_reason_non_calendar_fiscal_year').toString();
            default:
                return reason;
        }
    }
}
